import { createContext } from 'react';
import {
	IItemForGetShortInfo,
	TFileId,
	TLanguageIDs,
	IFileData,
	IServerError,
	IGameApplicationIdValidationError,
	ICreateGame,
	IGameTranslation,
} from 'types';

export type TCreateGameVisibleForm = 'mainForm' | 'translations';
export type TSourceUrlLocally = Nullable<string>;
export type TCreateGameSelectedItem = Nullable<string>;
export type TTypeOfCreateGameModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'imgSizeExceeded'
	| 'fileSizeExceeded'
	| 'fileUploadError'
	| 'invalidFileFormat'
	| 'someTranslationIsEmpty'
	| 'createDeveloper';

export interface ICreateGameState {
	visibleForm: TCreateGameVisibleForm;
	fileId: TFileId;
	sourceUrlLocally: TSourceUrlLocally;
	fetchedDevelopers: IItemForGetShortInfo[];
	IDSelectedDeveloper: TCreateGameSelectedItem;
	needRefetchDevelopers: boolean;
	fetchedCategories: IItemForGetShortInfo[];
	IDSelectedCategory: TCreateGameSelectedItem;
	applicationId: string;
	name: string;
	versionCode: string;
	versionName: string;
	description: string;
	appStoreLink: string;
	playMarketLink: string;
	fileData: IFileData;
	fetchedLanguages: IItemForGetShortInfo[];
	IDsSelectedLanguages: TLanguageIDs[];
	dataForSubmit: ICreateGame | null;
	typeOfModalWindow: TTypeOfCreateGameModalWindow;
	errorResponseData: IServerError<IGameApplicationIdValidationError> | null;
	selectedTranslationTab: TLanguageIDs | null;
	translations: IGameTranslation[];
}

export const createGameInitialState: ICreateGameState = {
	visibleForm: 'mainForm',
	fileId: null,
	sourceUrlLocally: null,
	fetchedDevelopers: [],
	IDSelectedDeveloper: null,
	needRefetchDevelopers: false,
	fetchedCategories: [],
	IDSelectedCategory: null,
	applicationId: '',
	name: '',
	versionCode: '',
	versionName: '',
	description: '',
	appStoreLink: '',
	playMarketLink: '',
	fileData: {
		fileId: '',
		fileHash: null,
		fileName: null,
	},
	fetchedLanguages: [],
	IDsSelectedLanguages: [],
	dataForSubmit: null,
	typeOfModalWindow: null,
	errorResponseData: null,
	selectedTranslationTab: null,
	translations: [],
};

export interface ICreateGameContext extends ICreateGameState {
	changeVisibleForm(value: TCreateGameVisibleForm): void;
	setFile(fileId: TFileId, sourceUrlLocally: TSourceUrlLocally): void;
	setFetchedDevelopers(value: IItemForGetShortInfo[]): void;
	setIDDeveloper(value: TCreateGameSelectedItem): void;
	refetchDevelopers(needRefetch: boolean): void;
	setFetchedCategories(value: IItemForGetShortInfo[]): void;
	setIDCategory(value: TCreateGameSelectedItem): void;
	changeApplicationId(value: string): void;
	changeName(value: string): void;
	changeVersionCode(value: string): void;
	changeVersionName(value: string): void;
	changeDescription(value: string): void;
	changeAppStoreLink(value: string): void;
	changePlayMarketLink(value: string): void;
	changeFileData(value: IFileData): void;
	setFetchedLanguages(value: IItemForGetShortInfo[]): void;
	setIDsLanguages(value: TLanguageIDs[]): void;
	setDataForSubmit(data: ICreateGame | null): void;
	changeTypeOfModalWindow(value: TTypeOfCreateGameModalWindow): void;
	showErrorModalWithInfo(value: IServerError<IGameApplicationIdValidationError> | null): void;
	setTranslationTab(value: TLanguageIDs | null): void;
	changeNameTranslation(languageId: TLanguageIDs, value: string): void;
	changeDescriptionTranslation(languageId: TLanguageIDs, value: string): void;
	resetFields(): void;
}

export const CreateGameContext = createContext<ICreateGameContext>({
	changeVisibleForm: () => undefined,
	setFile: () => undefined,
	setFetchedDevelopers: () => undefined,
	setIDDeveloper: () => undefined,
	refetchDevelopers: () => undefined,
	setFetchedCategories: () => undefined,
	setIDCategory: () => undefined,
	changeApplicationId: () => undefined,
	changeName: () => undefined,
	changeVersionCode: () => undefined,
	changeVersionName: () => undefined,
	changeDescription: () => undefined,
	changeAppStoreLink: () => undefined,
	changePlayMarketLink: () => undefined,
	changeFileData: () => undefined,
	setFetchedLanguages: () => undefined,
	setIDsLanguages: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	setTranslationTab: () => undefined,
	changeNameTranslation: () => undefined,
	changeDescriptionTranslation: () => undefined,
	resetFields: () => undefined,
	...createGameInitialState,
});
